import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, X, DeviceMobile, Share, Plus, Info } from "@phosphor-icons/react";

/**
 * Universal PWA install prompt.
 *
 * Behavior:
 * - Captures Chrome's `beforeinstallprompt` and reuses it on demand.
 * - Also exposes a manual trigger via the custom "bodje-open-install" event
 *   so a footer/nav button can summon the banner on any platform.
 * - When no native prompt is available (iOS Safari, Firefox desktop),
 *   shows a clear step-by-step instruction block instead of silently
 *   doing nothing.
 */

const DISMISS_KEY = "pwa-install-dismissed";
const COOLDOWN_MS = 14 * 24 * 3600 * 1000; // 14 days

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const safariIOS = iOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  if (safariIOS) return "ios";
  if (iOS) return "ios_other"; // iOS non-Safari — install not supported
  if (android) return "android";
  return "desktop";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [manual, setManual] = useState(false);
  const platform = detectPlatform();

  // Capture beforeinstallprompt (Chrome / Edge / Samsung Internet)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // auto-show once per cooldown
      const dismissed = localStorage.getItem(DISMISS_KEY);
      const freshEnough = dismissed && Date.now() - Number(dismissed) < COOLDOWN_MS;
      if (!freshEnough) setVisible(true);
    };
    const installed = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  // Manual summon from any button in the app
  useEffect(() => {
    const open = () => {
      setManual(true);
      setVisible(true);
    };
    window.addEventListener("bodje-open-install", open);
    return () => window.removeEventListener("bodje-open-install", open);
  }, []);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
    }
  }, [deferredPrompt]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setManual(false);
  };

  if (!visible || isStandalone()) return null;

  // Clear instructions block — shown either when the user manually opened
  // the dialog, OR when no native prompt is available.
  const nativeAvailable = !!deferredPrompt;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border-2 border-primary bg-card shadow-2xl md:left-auto md:right-4 md:bottom-4 md:w-[26rem]"
    >
      <div className="relative p-4">
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Fermer"
          className="btn-pill absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-muted text-foreground/70 hover:bg-destructive/15 hover:text-destructive"
        >
          <X size={14} weight="bold" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <DeviceMobile size={22} weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-base font-black leading-tight">
              Installer BODJE IMMO sur votre téléphone
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Accédez à vos annonces, messages et notifications depuis une icône dédiée sur l'écran d'accueil.
            </p>

            {nativeAvailable ? (
              <Button
                onClick={install}
                data-testid="pwa-install-btn"
                className="btn-pill mt-3 w-full rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
              >
                <DownloadSimple size={18} className="mr-1" weight="fill" /> Installer maintenant
              </Button>
            ) : platform === "ios" ? (
              <IosSteps />
            ) : platform === "ios_other" ? (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="flex items-center gap-1 font-bold">
                  <Info size={14} weight="fill" /> Ouvrez cette page dans <b>Safari</b>
                </div>
                <p className="mt-1">
                  L'installation sur iPhone n'est possible que depuis Safari. Appuyez sur <b>⋮</b> → <b>Ouvrir dans Safari</b>, puis réessayez.
                </p>
              </div>
            ) : platform === "android" ? (
              <AndroidFallbackSteps />
            ) : (
              <div className="mt-3 rounded-xl border border-border bg-muted/60 p-3 text-xs">
                <div className="font-bold">Depuis votre ordinateur</div>
                <p className="mt-1">
                  Dans Chrome ou Edge, cliquez sur l'icône <b>⊕</b> à droite de la barre d'adresse, puis <b>Installer</b>.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function IosSteps() {
  return (
    <ol className="mt-3 space-y-1.5 text-xs" data-testid="ios-steps">
      <li className="flex items-center gap-2">
        <Step n={1} />
        <span>
          Appuyez sur{" "}
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-semibold">
            <Share size={12} weight="bold" /> Partager
          </span>{" "}
          en bas de Safari
        </span>
      </li>
      <li className="flex items-center gap-2">
        <Step n={2} />
        <span>
          Choisissez{" "}
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-semibold">
            <Plus size={12} weight="bold" /> Sur l'écran d'accueil
          </span>
        </span>
      </li>
      <li className="flex items-center gap-2">
        <Step n={3} />
        <span>
          Appuyez sur <span className="rounded-md bg-muted px-1.5 py-0.5 font-semibold">Ajouter</span>
        </span>
      </li>
    </ol>
  );
}

function AndroidFallbackSteps() {
  return (
    <ol className="mt-3 space-y-1.5 text-xs" data-testid="android-steps">
      <li className="flex items-center gap-2">
        <Step n={1} />
        <span>Ouvrez le menu Chrome en haut à droite (⋮)</span>
      </li>
      <li className="flex items-center gap-2">
        <Step n={2} />
        <span>
          Appuyez sur <b>Installer l'application</b> ou <b>Ajouter à l'écran d'accueil</b>
        </span>
      </li>
      <li className="flex items-center gap-2">
        <Step n={3} />
        <span>Confirmez — l'icône BODJE IMMO apparaît sur votre écran d'accueil</span>
      </li>
    </ol>
  );
}

function Step({ n }) {
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-black text-secondary-foreground">
      {n}
    </span>
  );
}

/** Fire a global event to summon the install banner from any button. */
export function triggerInstallBanner() {
  window.dispatchEvent(new CustomEvent("bodje-open-install"));
}
